import TemplatesTable from "./TemplatesTable/TemplatesTable";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useContext, useEffect } from "react";
import { DesignTemplatesEndPointLogic } from "../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import SharedStateContext from "../../../SharedStateProvider";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import store from "../../../../store";
import {
  setSelectedComponents,
  setSelectedFeatures,
  setSelectedSharedUid,
  setSelectedSite,
  setSiteOptions,
} from "../../../../features/templateSlice";
import { useDispatch } from "react-redux";
import { useSearchParams, useLocation } from "react-router-dom";
import SimpleBackdrop from "../../../BackDrop/BackDrop";

interface TemplateProps {
  oauthTokenHeader: any;
  response: any;
}

function Template({ oauthTokenHeader }: TemplateProps) {
  const tableHeadings = {
    name: "Name",
    component: "Component Type",
    modified: "Last Updated",
    labels: "Features",
    quick_actions: "Quick Actions",
  };

  const response = useSelector(selectUserData);
  const [dataShow, setDataShow] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(false);
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [tableRefresh, setTableRefresh] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedSharedUid, selectedSite, siteOptions } = useSelector(
    (state: any) => state.template
  );
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (response?.data?.website_permissions) {
      const siteOptions = response.data.website_permissions.map(
        (siteObj: any) => {
          const uid = Object.keys(siteObj)[0]; // UID of the site
          const siteName = siteObj[uid].ui_site_name; // Site name
          return { uid, siteName }; // Return as an object with uid and siteName
        }
      );

      store.dispatch(setSiteOptions(siteOptions)); // Dispatch the memoized options
    }
  }, [response]); // Runs when `response` changes

  useEffect(() => {
    if (!selectedSharedUid) {
      setSearch(false);
      setDataShow(false);
    } else if (selectedSite) {
      handleSearch();
    }
    setLoad(false);
    // eslint-disable-next-line
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite");
    const updatedParams = new URLSearchParams(searchParams);

    // Remove templateSharedUid from URL
    updatedParams.delete("templateSharedUid");
    setSearchParams(updatedParams, { replace: true });

    if (siteFromURL) {
      const isValidSite = siteOptions?.find(
        (site: any) => site.uid === siteFromURL
      );

      if (isValidSite) {
        store.dispatch(setSelectedSharedUid(siteFromURL)); // Set the selected site
        setSearch(true);
        setDataShow(true);
      } else {
        // Redirect to home if invalid site
        updatedParams.delete("selectedSite");
      }
    }
    setSearchParams(updatedParams, { replace: true });
    // eslint-disable-next-line
  }, [siteOptions]);

  // below portion is for setting and deleting the site
  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get existing params

    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the value
    } else {
      updatedParams.delete("selectedSite"); // Remove key if null
    }

    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading
  };

  const handleSearch = () => {
    if (selectedSharedUid) {
      setSearch(true);
      setDataShow(true);
      store.dispatch(setSelectedSite(selectedSharedUid));
      // Update the URL with selected site's UID
      updateURLParams(selectedSharedUid);
    }
  };

  const handleSelectWebsite = (_: unknown, newValue: any) => {
    const params = new URLSearchParams(location.search);

    if (!newValue) {
      store.dispatch(setSelectedSharedUid(null));
      setDataShow(false);
      updateURLParams(null); // Remove selectedSite from URL
    } else {
      store.dispatch(setSelectedSharedUid(newValue.uid));
      setSearch(false);
      store.dispatch(setSelectedSite(null));
      setDataShow(false); // Reset dataShow when a new site is selected
      store.dispatch(setSelectedFeatures(null));
      store.dispatch(setSelectedComponents(null));

      // Remove search and filter params when selecting a new site
      params.delete("search");
      params.delete("filters");
      setSearchParams(params);
    }
  };

  useEffect(() => {
    setDataLoaded(false);
    setTableRefresh(false);

    if (!search || !dataShow || !selectedSharedUid) return;
    setLoading(true);

    const fetchTemplates = async () => {
      try {
        await DesignTemplatesEndPointLogic.getDesignTemplates(
          oauthTokenHeader,
          selectedSharedUid,
          dispatch
        );
      } catch (error) {
        console.error("Error fetching design templates:", error);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    fetchTemplates();

    // eslint-disable-next-line
  }, [dataShow, selectedSharedUid, tableRefresh]);

  useEffect(() => {
    const hasSelectedSite = searchParams.has("selectedSite");

    if (!hasSelectedSite && selectedSharedUid !== null) {
      dispatch(setSelectedSharedUid(null));
      dispatch(setSelectedSite(null));
      setDataShow(false);
      setSearch(false);
    }
    // Run this effect only when the searchParams or siteOptions change
    // eslint-disable-next-line
  }, [searchParams, dispatch]);

  return (
    <>
      <Paper sx={{ padding: "8px 16px" }}>
        <Typography fontSize={24}>Templates</Typography>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          gap={4}
          mt={2}
        >
          <Autocomplete
            disablePortal
            sx={{ width: "100%" }}
            options={siteOptions}
            getOptionLabel={(option) => option.siteName}
            value={
              siteOptions.find(
                (option: any) => option?.uid === selectedSharedUid
              ) || null
            }
            onChange={handleSelectWebsite}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search site name or choose from the list"
                label="Select Website"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            data-tracker="146"
            disabled={!selectedSharedUid}
            color="secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            SELECT SITE
          </Button>
        </Stack>
      </Paper>
      {dataShow && (
        <>
          {loading ? (
            <SimpleBackdrop />
          ) : dataLoaded ? (
            <>
              <TemplatesTable
                tableHeadings={tableHeadings}
                setSelectedSharedUid={setSelectedSharedUid}
                setLoading={setLoading}
                loading={loading}
              />
            </>
          ) : null}
        </>
      )}
    </>
  );
}

export default Template;
